// Welle 64 (SIJ-635): Tool „Protokolle" — Teams-Transkript → BCG-Protokoll (DOCX).
//
// Ablauf ohne bezahlte KI:
//   1. Transkript-.docx hochladen → Backend parst deterministisch
//      (Kopf, Berlin-Zeit, Teilnehmer, Mitschrift-Segmente) + baut Copy-Prompt.
//   2. Prompt kopieren → in Gemini/KI einfügen → Markdown-Antwort (Kapitel 1-3)
//      hier ins Textfeld einfügen.
//   3. „DOCX erstellen" → fertiges Protokoll herunterladen.
//      (Geht auch ohne KI-Antwort — dann mit Platzhalter-Kapiteln.)
import React, { useEffect, useRef, useState } from "react";
import { API_BASE } from "../api/config";
import { pFetch, authQuery } from "../portal/portalAuth";

const API = `${API_BASE}/protokolle`;

const box = {
  background: "var(--color-surface, #1a1a2e)",
  border: "1px solid rgba(124,106,247,0.35)",
  borderRadius: 8, padding: 16, marginBottom: 16,
};
const btn = {
  padding: "8px 14px", borderRadius: 6, border: "1px solid rgba(124,106,247,0.6)",
  background: "#2a2a4a", color: "#eee", cursor: "pointer", fontSize: "0.9rem",
};
const btnPrimary = { ...btn, background: "#2e7d32", border: "none", fontWeight: 600 };
const inp = {
  width: "100%", boxSizing: "border-box", padding: "6px 8px",
  background: "#111", color: "#eee", border: "1px solid #333", borderRadius: 4,
};
const lbl = { display: "block", fontSize: "0.8rem", opacity: 0.75, margin: "10px 0 3px" };

export default function ProtokollePage({ portal = false, onBack, backLabel }) {
  const [meta, setMeta] = useState(null);       // Ergebnis von /parse (editierbar)
  const [suffix, setSuffix] = useState("");
  const [projekt, setProjekt] = useState("");
  const [kiMarkdown, setKiMarkdown] = useState("");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");
  const [ergebnis, setErgebnis] = useState(null); // {id, filename, download_url}
  const [copied, setCopied] = useState(false);
  const [geminiBusy, setGeminiBusy] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchMsg, setBatchMsg] = useState("");
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setFehler(""); setErgebnis(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await pFetch(`${API}/parse`, { method: "POST", body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setMeta(d);
      setSuffix(d.suffix_vorschlag || "");
      setKiMarkdown("");
    } catch (e) {
      setFehler(String(e.message || e));
      setMeta(null);
    } finally {
      setBusy(false);
    }
  };

  const kopierePrompt = async () => {
    if (!meta?.prompt) return;
    try {
      await navigator.clipboard.writeText(meta.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: Textarea markieren
      window.prompt("Prompt manuell kopieren:", meta.prompt.slice(0, 500) + " …");
    }
  };

  // SIJ-635: Ordner als Hintergrund-Job — je Datei einzeln an Gemini, Fortschritt per Polling,
  // jedes DOCX einzeln herunterladbar und optional direkt in Dropbox.
  const [batchJob, setBatchJob] = useState(null);          // Status-Objekt von /batch/status
  const [dropboxSpeichern, setDropboxSpeichern] = useState(true);
  const [dropboxOrdner, setDropboxOrdner] = useState("");
  const [dropboxVerfuegbar, setDropboxVerfuegbar] = useState(false);
  const [batchMitGemini, setBatchMitGemini] = useState(true);
  const pollRef = useRef(null);

  useEffect(() => {
    pFetch(`${API}/config`).then((r) => r.json()).then((d) => {
      if (d?.dropbox_standard_ordner) setDropboxOrdner(d.dropbox_standard_ordner);
      setDropboxVerfuegbar(!!d?.dropbox_verfuegbar);
      if (!d?.dropbox_verfuegbar) setDropboxSpeichern(false);
    }).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, []);

  const pollen = (jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const d = await (await pFetch(`${API}/batch/status?job_id=${encodeURIComponent(jobId)}`)).json();
        setBatchJob(d);
        if (d.status !== "running") {
          clearInterval(pollRef.current);
          setBatchBusy(false);
          if (d.status === "unbekannt" || d.status === "abgebrochen") {
            setBatchMsg(`Job ${d.status} — Server neu gestartet? Bitte erneut starten.`);
          } else {
            setBatchMsg(`✓ ${d.ok_count}/${d.total} Protokolle erstellt` + (d.fehler_count ? ` · ${d.fehler_count} Fehler` : ""));
          }
        }
      } catch (e) { /* weiter pollen */ }
    }, 1500);
  };

  const verarbeiteOrdner = async (fileList) => {
    const dateien = Array.from(fileList || []).filter((f) => f.name.toLowerCase().endsWith(".docx"));
    if (dateien.length === 0) { setBatchMsg("Keine .docx-Dateien gefunden"); return; }
    setBatchBusy(true); setBatchMsg(""); setBatchJob(null);
    try {
      const fd = new FormData();
      dateien.forEach((f) => fd.append("files", f));
      fd.append("projekt", projekt || "");
      fd.append("mit_gemini", batchMitGemini ? "true" : "false");
      fd.append("dropbox_speichern", dropboxSpeichern && dropboxVerfuegbar ? "true" : "false");
      fd.append("dropbox_ordner", dropboxOrdner || "");
      const r = await pFetch(`${API}/batch/start`, { method: "POST", body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setBatchJob({ job_id: d.job_id, status: "running", total: d.total, done: 0, ergebnisse: [] });
      pollen(d.job_id);
    } catch (e) {
      setBatchMsg("Fehler: " + String(e.message || e));
      setBatchBusy(false);
    }
  };

  const direktAnGemini = async () => {
    if (!meta) return;
    setGeminiBusy(true); setFehler("");
    try {
      const r = await pFetch(`${API}/gemini`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: meta.prompt || "" }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setKiMarkdown(d.markdown || "");
    } catch (e) {
      setFehler(String(e.message || e));
    } finally {
      setGeminiBusy(false);
    }
  };

  const erzeugen = async () => {
    if (!meta) return;
    setBusy(true); setFehler(""); setErgebnis(null);
    try {
      const body = {
        titel: meta.titel, datum: meta.datum, datum_iso: meta.datum_iso,
        start_berlin: meta.start_berlin, start_utc: meta.start_utc,
        ende_berlin: meta.ende_berlin, tz_name: meta.tz_name,
        dauer: meta.dauer, teilnehmer: meta.teilnehmer || [],
        segmente: meta.segmente || [],
        suffix, projekt, ki_markdown: kiMarkdown,
      };
      const r = await pFetch(`${API}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setErgebnis(d);
      window.open(`${API_BASE}${d.download_url}&${authQuery()}`, "_blank");
    } catch (e) {
      setFehler(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const setMetaFeld = (key, val) => setMeta((m) => ({ ...m, [key]: val }));

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", padding: 20, color: "#eee" }}>
      {onBack && (
        <button style={{ ...btn, marginBottom: 12 }} onClick={onBack}>← {backLabel || "Zurück"}</button>
      )}
      <h2 style={{ margin: "0 0 4px" }}>📝 Protokolle</h2>
      <p style={{ opacity: 0.7, marginTop: 0, fontSize: "0.9rem" }}>
        MS-Teams-Transkript (.docx) hochladen → Prompt in die KI deiner Wahl kopieren →
        Antwort einfügen → fertiges BCG-Protokoll als DOCX.
      </p>

      {/* Schritt 1: Upload */}
      <div style={box}>
        <strong>1. Transkript hochladen</strong>
        <div style={{ marginTop: 10 }}>
          <input
            ref={fileRef} type="file" accept=".docx"
            onChange={(e) => upload(e.target.files?.[0])}
            disabled={busy}
          />
        </div>
        {busy && !meta && <p style={{ opacity: 0.7 }}>Wird gelesen…</p>}
      </div>

      {/* Ganzer Ordner / mehrere Dateien → Job je Datei (SIJ-635) */}
      <div style={box}>
        <strong>Oder: ganzer Ordner (mehrere Transkripte)</strong>
        <p style={{ opacity: 0.7, margin: "6px 0", fontSize: "0.8rem" }}>
          Mehrere .docx auswählen — jede wird <b>einzeln nacheinander</b> an Gemini geschickt.
          Fertige Protokolle kannst du einzeln herunterladen und/oder direkt in Dropbox ablegen.
        </p>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", margin: "6px 0 10px", fontSize: "0.85rem" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={batchMitGemini} disabled={batchBusy} onChange={(e) => setBatchMitGemini(e.target.checked)} />
            ✨ mit Gemini
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: dropboxVerfuegbar ? "pointer" : "not-allowed", opacity: dropboxVerfuegbar ? 1 : 0.5 }}
            title={dropboxVerfuegbar ? "" : "Dropbox nicht konfiguriert"}>
            <input type="checkbox" checked={dropboxSpeichern && dropboxVerfuegbar} disabled={batchBusy || !dropboxVerfuegbar}
              onChange={(e) => setDropboxSpeichern(e.target.checked)} />
            in Dropbox speichern
          </label>
        </div>
        {dropboxSpeichern && dropboxVerfuegbar && (
          <div style={{ marginBottom: 10 }}>
            <label style={lbl}>Dropbox-Ordner (frei wählbar, Standard vorbelegt)</label>
            <input style={inp} value={dropboxOrdner} disabled={batchBusy}
              onChange={(e) => setDropboxOrdner(e.target.value)} placeholder="/Holthey Immobilien/…/Protokolle" />
          </div>
        )}
        <input
          type="file" accept=".docx" multiple
          disabled={batchBusy}
          onChange={(e) => { const fl = e.target.files; if (fl && fl.length) verarbeiteOrdner(fl); e.target.value = ""; }}
        />
        {batchJob && (
          <div style={{ marginTop: 10 }}>
            {batchJob.status === "running" && (
              <p style={{ opacity: 0.85, margin: "4px 0" }}>
                ⏳ Datei {Math.min((batchJob.done || 0) + 1, batchJob.total)}/{batchJob.total}
                {batchJob.current_datei ? `: ${batchJob.current_datei}` : ""}
              </p>
            )}
            {(batchJob.ergebnisse || []).length > 0 && (
              <div style={{ marginTop: 6, fontSize: "0.82rem" }}>
                {batchJob.ergebnisse.map((r, i) => (
                  <div key={i} style={{ padding: "4px 0", borderTop: "1px solid #2a2a4a", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ color: r.ok ? "#7fff7f" : "#ef9a9a" }}>{r.ok ? "✓" : "✗"}</span>
                    <span style={{ opacity: 0.85, wordBreak: "break-all" }}>{r.ausgabe || r.datei}</span>
                    {r.download_url && (
                      <a href={`${API_BASE}${r.download_url}&${authQuery()}`} target="_blank" rel="noreferrer"
                        style={{ color: "#aaaaff" }}>⬇ Download</a>
                    )}
                    {r.dropbox_pfad && <span style={{ opacity: 0.6 }} title={r.dropbox_pfad}>☁ Dropbox</span>}
                    {r.fehler && <span style={{ color: "#ef9a9a" }}>{r.fehler}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {batchMsg && <p style={{ marginTop: 8, color: batchMsg.startsWith("✓") ? "#7fff7f" : "#ef9a9a" }}>{batchMsg}</p>}
      </div>

      {fehler && (
        <div style={{ ...box, borderColor: "#c62828", color: "#ef9a9a" }}>{fehler}</div>
      )}

      {meta && (
        <>
          {/* Kopf-Daten (editierbar) */}
          <div style={box}>
            <strong>2. Kopf-Daten prüfen</strong>
            <label style={lbl}>Meeting-Titel</label>
            <input style={inp} value={meta.titel || ""} onChange={(e) => setMetaFeld("titel", e.target.value)} />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 120 }}>
                <label style={lbl}>Datum</label>
                <input style={inp} value={meta.datum || ""} onChange={(e) => setMetaFeld("datum", e.target.value)} />
              </div>
              <div style={{ flex: 1, minWidth: 100 }}>
                <label style={lbl}>Start (Berlin)</label>
                <input style={inp} value={meta.start_berlin || ""} onChange={(e) => setMetaFeld("start_berlin", e.target.value)} />
              </div>
              <div style={{ flex: 1, minWidth: 100 }}>
                <label style={lbl}>Ende (Berlin)</label>
                <input style={inp} value={meta.ende_berlin || ""} onChange={(e) => setMetaFeld("ende_berlin", e.target.value)} />
              </div>
              <div style={{ flex: 1, minWidth: 130 }}>
                <label style={lbl}>Dauer</label>
                <input style={inp} value={meta.dauer || ""} onChange={(e) => setMetaFeld("dauer", e.target.value)} />
              </div>
            </div>
            <label style={lbl}>Teilnehmer (Komma-getrennt)</label>
            <input
              style={inp}
              value={(meta.teilnehmer || []).join(", ")}
              onChange={(e) => setMetaFeld("teilnehmer", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label style={lbl}>Projekt (Kopfzeile, optional)</label>
                <input style={inp} value={projekt} onChange={(e) => setProjekt(e.target.value)}
                       placeholder="z. B. Volkswohl Bund – M/Text (Gems Testmanagement)" />
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <label style={lbl}>Namens-Suffix (Dateiname/Titel)</label>
                <input style={inp} value={suffix} onChange={(e) => setSuffix(e.target.value)}
                       placeholder='z. B. "Testerdaily" oder "0800"' />
              </div>
            </div>
            <p style={{ fontSize: "0.78rem", opacity: 0.6, margin: "8px 0 0" }}>
              Erkannt: {meta.segmente?.length || 0} Mitschrift-Segment(e) — landen automatisch in
              Kapitel 4. Dateiname: <code>Protokoll - {meta.datum_iso} {(meta.start_berlin || "").replace(":", "-")} - {suffix || "…"}.docx</code>
            </p>
          </div>

          {/* Schritt 3: Prompt */}
          <div style={box}>
            <strong>3. Prompt für die KI (Kapitel 1–3)</strong>
            <p style={{ fontSize: "0.8rem", opacity: 0.7, margin: "6px 0" }}>
              In Gemini (oder eine andere KI) einfügen — die Markdown-Antwort unten in Schritt 4 einsetzen.
            </p>
            <textarea
              readOnly value={meta.prompt || ""}
              style={{ ...inp, height: 120, fontFamily: "monospace", fontSize: "0.72rem" }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              <button style={btn} onClick={kopierePrompt}>
                {copied ? "✓ Kopiert" : "📋 Prompt kopieren"}
              </button>
              <button style={{ ...btnPrimary, background: "#7c4dff" }} disabled={geminiBusy} onClick={direktAnGemini}>
                {geminiBusy ? "✨ Gemini arbeitet…" : "✨ Direkt an Gemini senden"}
              </button>
            </div>
            <p style={{ fontSize: "0.72rem", opacity: 0.55, margin: "6px 0 0" }}>
              „Direkt an Gemini" schickt das Transkript automatisch an Gemini und füllt Schritt 4 mit der Antwort.
            </p>
          </div>

          {/* Schritt 4: KI-Antwort */}
          <div style={box}>
            <strong>4. KI-Antwort einfügen (optional)</strong>
            <p style={{ fontSize: "0.8rem", opacity: 0.7, margin: "6px 0" }}>
              Ohne Antwort werden Kapitel 1–3 als Platzhalter eingefügt — Kapitel 4 (Mitschrift) kommt immer automatisch.
            </p>
            <textarea
              value={kiMarkdown} onChange={(e) => setKiMarkdown(e.target.value)}
              placeholder="## 1. Management Overview …"
              style={{ ...inp, height: 180, fontFamily: "monospace", fontSize: "0.78rem" }}
            />
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button style={btnPrimary} disabled={busy} onClick={erzeugen}>
              {busy ? "Erzeuge…" : "📄 DOCX erstellen"}
            </button>
            {ergebnis && (
              <a
                style={{ color: "#81c784" }}
                href={`${API_BASE}${ergebnis.download_url}&${authQuery()}`}
                target="_blank" rel="noreferrer"
              >
                ⬇ {ergebnis.filename}
              </a>
            )}
          </div>
        </>
      )}
    </div>
  );
}
